<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import AppButton from '@/components/primitives/AppButton/AppButton.vue'
import { useInboxStore } from '@/stores/inbox'
import { useToastStore } from '@/stores/toast'
import type { InboxThread } from '@/types/inbox'

import InboxNoticeCard from './InboxNoticeCard.vue'
import {
  INBOX_ROUTE_NAMES,
  NOTICE_VISIBLE_CAP,
  type SettledNotices,
  pendingNotices,
  settleNotices,
  stackNotices,
} from './inbox-notices'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const inbox = useInboxStore()
const toast = useToastStore()

const watermark = ref<Date | null>(null)
const settled = ref<SettledNotices>(new Map())

watch(
  () => inbox.shellLoaded,
  (loaded) => {
    watermark.value = loaded ? (watermark.value ?? new Date()) : null
    if (!loaded) settled.value = new Map()
  },
  { immediate: true },
)

const onInboxRoute = computed(() => INBOX_ROUTE_NAMES.includes(String(route.name)))

const pending = computed(() =>
  watermark.value
    ? pendingNotices(inbox.shellThreads, inbox.readMarks, watermark.value, settled.value)
    : [],
)

const stack = computed(() =>
  onInboxRoute.value
    ? stackNotices([], NOTICE_VISIBLE_CAP)
    : stackNotices(pending.value, NOTICE_VISIBLE_CAP),
)

watch(
  [pending, onInboxRoute],
  ([threads, suppressed]) => {
    if (suppressed && threads.length > 0) settle(threads)
  },
  { immediate: true },
)

function settle(threads: readonly InboxThread[]): void {
  settled.value = settleNotices(settled.value, threads)
}

function open(thread: InboxThread): void {
  settle([thread])
  void router.push({ name: 'inbox', query: { item: thread.id } })
}

function later(thread: InboxThread): void {
  settle([thread])
  toast.success(t('inbox.toast.later'))
}

function openInbox(): void {
  settle(pending.value)
  void router.push({ name: 'inbox' })
}
</script>

<template>
  <TransitionGroup
    tag="ol"
    enter-active-class="motion-safe:animate-slide-in-from-right"
    leave-active-class="motion-safe:animate-fade-out"
    class="fixed top-12 right-12 z-50 m-0 flex w-(--toast-width) max-w-[calc(100vw-var(--spacing-16))] list-none flex-col items-end gap-6 p-0"
  >
    <li v-for="thread in stack.shown" :key="thread.id">
      <InboxNoticeCard
        :thread="thread"
        @open="open(thread)"
        @later="later(thread)"
        @expire="settle([thread])"
      />
    </li>
    <li v-if="stack.folded > 0" key="folded">
      <AppButton
        size="xs"
        variant="secondary"
        :label="t('inbox.more', { n: stack.folded })"
        @click="openInbox"
      />
    </li>
  </TransitionGroup>
</template>
